import React, { useState } from 'react';
import api from '@/utils/api';
import './AccessAudit.scss';

interface AccessRecord {
  wallet: string;
  db: {
    hasViewAccess: boolean;
    hasSubscription: boolean;
    subscriptionExpiresAt: string | null;
  };
  chain: {
    hasViewAccess: boolean;
    hasSubscription: boolean;
    subscriptionExpiresAt: string | null;
  };
  viewMismatch: boolean;
  subMismatch: boolean;
  hasMismatch: boolean;
}

interface AuditResult {
  profileId: number;
  total: number;
  mismatches: number;
  records: AccessRecord[];
}

const AccessAuditPage: React.FC = () => {
  const [profileIdInput, setProfileIdInput] = useState('');
  const [result, setResult] = useState<AuditResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');

  const handleAudit = async (e: React.FormEvent) => {
    e.preventDefault();
    const profileId = parseInt(profileIdInput.trim(), 10);
    if (isNaN(profileId) || profileId <= 0) {
      setError('Enter a valid on-chain profile ID (positive integer).');
      return;
    }
    setIsLoading(true);
    setError('');
    setSyncMessage('');
    setResult(null);
    try {
      const { data } = await api.get(`/admin/access/audit/${profileId}`);
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Audit failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    if (!result) return;
    setIsSyncing(true);
    setError('');
    setSyncMessage('');
    try {
      const { data } = await api.post(`/admin/access/sync/${result.profileId}`);
      setSyncMessage(data.message);
      // Re-run audit to refresh
      const { data: fresh } = await api.get(`/admin/access/audit/${result.profileId}`);
      setResult(fresh);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const tick = (val: boolean) => (val ? '✓' : '✗');
  const tickClass = (val: boolean) => (val ? 'tick-yes' : 'tick-no');

  return (
    <div className="access-audit">
      <div className="audit-header">
        <div>
          <h1>Profile Access Audit</h1>
          <p className="subtitle">
            Compare DB-cached access state vs live on-chain state for any profile.
            Mismatches indicate DB/chain drift that may cause incorrect paywall behaviour.
          </p>
        </div>
      </div>

      <form className="audit-form" onSubmit={handleAudit}>
        <label htmlFor="profileId">On-chain Profile ID</label>
        <div className="audit-form-row">
          <input
            id="profileId"
            type="number"
            min="1"
            value={profileIdInput}
            onChange={(e) => setProfileIdInput(e.target.value)}
            placeholder="e.g. 42"
            className="audit-input"
          />
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Auditing…' : 'Run Audit'}
          </button>
        </div>
        <p className="audit-hint">
          This is the on-chain numeric ID (nftProfileId), not the DB UUID.
        </p>
      </form>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}
      {syncMessage && (
        <div className="alert alert-success">
          <span>✓</span> {syncMessage}
        </div>
      )}

      {result && (
        <>
          <div className="audit-summary">
            <div className={`summary-card ${result.mismatches > 0 ? 'card-warn' : 'card-ok'}`}>
              <span className="summary-label">Wallets in DB</span>
              <span className="summary-value">{result.total}</span>
            </div>
            <div className={`summary-card ${result.mismatches > 0 ? 'card-warn' : 'card-ok'}`}>
              <span className="summary-label">Mismatches</span>
              <span className="summary-value">{result.mismatches}</span>
            </div>
            <div className="summary-card card-neutral">
              <span className="summary-label">Profile ID</span>
              <span className="summary-value">{result.profileId}</span>
            </div>
          </div>

          {result.mismatches > 0 && (
            <div className="sync-bar">
              <p>
                {result.mismatches} mismatch{result.mismatches !== 1 ? 'es' : ''} found. Sync will
                write chain state into the DB for wallets where chain = granted but DB = not granted.
              </p>
              <button
                className="btn btn-secondary"
                onClick={handleSync}
                disabled={isSyncing}
              >
                {isSyncing ? 'Syncing…' : `Sync ${result.mismatches} mismatch${result.mismatches !== 1 ? 'es' : ''} → DB`}
              </button>
            </div>
          )}

          {result.records.length === 0 ? (
            <p className="no-records">No access records found in DB for this profile.</p>
          ) : (
            <div className="audit-table-wrap">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Wallet</th>
                    <th colSpan={2}>View Access</th>
                    <th colSpan={3}>Subscription</th>
                    <th>Status</th>
                  </tr>
                  <tr className="subheader">
                    <th />
                    <th>DB</th>
                    <th>Chain</th>
                    <th>DB</th>
                    <th>Chain</th>
                    <th>Expires (chain)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {result.records.map((r) => (
                    <tr key={r.wallet} className={r.hasMismatch ? 'row-mismatch' : ''}>
                      <td className="cell-wallet">
                        <span title={r.wallet}>
                          {r.wallet.slice(0, 10)}…{r.wallet.slice(-6)}
                        </span>
                      </td>
                      <td className={`cell-bool ${tickClass(r.db.hasViewAccess)}`}>
                        {tick(r.db.hasViewAccess)}
                      </td>
                      <td className={`cell-bool ${tickClass(r.chain.hasViewAccess)} ${r.viewMismatch ? 'cell-mismatch' : ''}`}>
                        {tick(r.chain.hasViewAccess)}
                        {r.viewMismatch && <span className="mismatch-badge">!</span>}
                      </td>
                      <td className={`cell-bool ${tickClass(r.db.hasSubscription)}`}>
                        {tick(r.db.hasSubscription)}
                        {r.db.hasSubscription && (
                          <span className="cell-expiry">{fmt(r.db.subscriptionExpiresAt)}</span>
                        )}
                      </td>
                      <td className={`cell-bool ${tickClass(r.chain.hasSubscription)} ${r.subMismatch ? 'cell-mismatch' : ''}`}>
                        {tick(r.chain.hasSubscription)}
                        {r.subMismatch && <span className="mismatch-badge">!</span>}
                      </td>
                      <td className="cell-expiry-chain">
                        {fmt(r.chain.subscriptionExpiresAt)}
                      </td>
                      <td>
                        {r.hasMismatch ? (
                          <span className="badge badge-warn">Mismatch</span>
                        ) : (
                          <span className="badge badge-ok">In sync</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccessAuditPage;
